// Modal de búsqueda global - Global search modal component
// Muestra resultados de búsqueda categorizados con navegación por teclado
import {
  faArrowDown,
  faArrowUp,
  faBagShopping,
  faBoxOpen,
  faCartFlatbed,
  faCartShopping,
  faFileLines,
  faBoxesStacked,
  faShelves,
  faStore,
  faXmark,
  faMagnifyingGlass,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Badge } from '@shared/components/ui/badge';
import { EnterIcon } from '@shared/components/ui/icons';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Spinner } from '@shared/components/ui/Spinner';
import * as Icons from '@shared/components/ui/icons/warehouse.icons';
import { StatusBadgeWithDot } from '@modules/Orders/List/components/display/StatusBadgeWithDot';
import { defaultOrderStatusDot, statusColorMap } from '@modules/Orders/List/config/orderStatusConfig';
import { MerchandiseStatusBadge } from '@modules/Merchandise/components/MerchandiseStatusBadge';
import { getPreparationStatusColor, usePreparationStatusName } from '@modules/OrderPreparation/shared/config';
import { getReturnedStatusBadgeClass, getReturnedStatusName } from '@modules/Returned/List/config/returnedConfig';

import { createGlobalSearchAdapter } from './adapters/GlobalSearchAdapter';
import type { GlobalSearchResult } from './models/GlobalSearch';
import { getGlobalSearchRoute } from './utils/getGlobalSearchRoute';

// Componente para badge de estado de preparación - Preparation status badge component
const PreparationStatusBadge: React.FC<{ status: number }> = ({ status }) => {
  const statusName = usePreparationStatusName(status);
  const statusColor = getPreparationStatusColor(status);

  return (
    <Badge variant="outline" className={`text-xs bg-card-border border-0 ${statusColor}`}>
      {statusName}
    </Badge>
  );
};

interface GlobalSearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
}

export const GlobalSearchModal: React.FC<GlobalSearchModalProps> = ({
  open,
  onOpenChange,
  searchQuery,
  onSearchQueryChange,
}) => {
  const { t } = useTranslation('menu');
  const navigate = useNavigate();
  const modalRef = useRef<ModalCenterRef>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const globalSearchAdapter = useMemo(() => createGlobalSearchAdapter(), []);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const resultsListRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Controlar el modal con el ref - Control modal with ref
  useEffect(() => {
    if (open) {
      modalRef.current?.openModal();
      // Enfocar el input cuando se abre el modal - Focus input when modal opens
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      modalRef.current?.closeModal();
    }
  }, [open]);

  // Manejar cambio en el input de búsqueda - Handle search input change
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearchQueryChange(e.target.value);
  };

  // Manejar reset de búsqueda - Handle search reset
  const handleResetSearch = () => {
    onSearchQueryChange('');
    searchInputRef.current?.focus();
  };

  // Búsqueda con infinite scroll usando React Query - Search with infinite scroll using React Query
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['globalSearch', searchQuery],
    queryFn: async ({ pageParam = 1 }) => {
      if (!searchQuery.trim()) {
        return {
          items: [],
          total: 0,
          currentPage: 1,
          lastPage: 1,
          hasNextPage: false,
          nextPage: null,
        };
      }
      return globalSearchAdapter.search(searchQuery, pageParam, 15);
    },
    enabled: open && searchQuery.trim().length > 0,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 0,
    gcTime: 0,
  });

  // Obtener todos los resultados en una lista plana para navegación - Get all results in a flat list for navigation
  const allResults = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap((page) => page.items);
  }, [data]);

  // Obtener el total de resultados de la primera página - Get total results from first page
  const totalResults = useMemo(() => {
    return data?.pages?.[0]?.total ?? 0;
  }, [data]);

  // Resetear índice seleccionado cuando cambia el query - Reset selected index when query changes
  useEffect(() => {
    if (searchQuery.trim()) {
      setSelectedIndex(0);
    }
  }, [searchQuery]);

  // Infinite scroll con IntersectionObserver - Infinite scroll with IntersectionObserver
  useEffect(() => {
    if (!open || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '200px', // Cargar cuando esté a 200px del final
      },
    );

    const loadMoreElement = loadMoreRef.current;
    if (loadMoreElement) {
      observer.observe(loadMoreElement);
    }

    return () => {
      if (loadMoreElement) {
        observer.unobserve(loadMoreElement);
      }
    };
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Manejar navegación con teclado - Handle keyboard navigation
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        // Quitar focus de cualquier elemento activo - Remove focus from any active element
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        setSelectedIndex((prev) => (prev < allResults.length - 1 ? prev + 1 : prev));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        // Quitar focus de cualquier elemento activo - Remove focus from any active element
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const selectedResult = allResults[selectedIndex];
        if (selectedResult) {
          const route = getGlobalSearchRoute(
            selectedResult.type,
            selectedResult.id,
            selectedResult.statusName,
            selectedResult.code,
          );
          // 🎯 Usar replace: false para forzar actualización incluso si la ruta base es la misma
          navigate(route, { replace: false });
          onSearchQueryChange(''); // 🎯 Limpiar el input de búsqueda
          onOpenChange(false);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open, allResults, selectedIndex, navigate, onOpenChange, onSearchQueryChange]);

  // Scroll al elemento seleccionado - Scroll to selected element
  useEffect(() => {
    if (resultsListRef.current && allResults.length > 0) {
      const selectedElement = resultsListRef.current.querySelector(`[data-result-index="${selectedIndex}"]`);
      selectedElement?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedIndex, allResults.length]);

  // Obtener icono según el tipo (reutilizando iconos del menú) - Get icon by type using menu icons
  const typeIcons = useMemo(
    () => ({
      product: <FontAwesomeIcon icon={faBagShopping} className="w-3 h-3 text-icons light:text-title" />,
      cart: <FontAwesomeIcon icon={faCartFlatbed} className="w-3 h-3 text-icons light:text-title" />,
      order: <FontAwesomeIcon icon={faCartShopping} className="w-3 h-3 text-icons light:text-title" />,
      merchandise: <FontAwesomeIcon icon={faBoxOpen} className="w-3 h-3 text-icons light:text-title" />,
      location: <FontAwesomeIcon icon={faShelves} className="w-3 h-3 text-icons light:text-title" />,
      preparation: <FontAwesomeIcon icon={faBoxOpen} className="w-3 h-3 text-icons light:text-title" />,
      returned_reception: <Icons.RehusableIcon className="w-3 h-3 text-icons light:text-title" />,
      default: <FontAwesomeIcon icon={faFileLines} className="w-3 h-3 text-icons light:text-title" />,
    }),
    [],
  );

  const getIconNode = (type: string) => (typeIcons as Record<string, React.ReactNode>)[type] || typeIcons.default;

  // Obtener label de categoría - Get category label
  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'product':
        return t('global_search.category.product');
      case 'cart':
        return t('global_search.category.cart');
      case 'order':
        return t('global_search.category.order');
      case 'merchandise':
        return t('global_search.category.merchandise');
      case 'location':
        return t('global_search.category.location');
      case 'preparation':
        return t('global_search.category.preparation');
      case 'returned_reception':
        return t('global_search.category.returned_reception');
      default:
        return category;
    }
  };

  // Mapear nombre de estado a ID numérico para obtener colores - Map status name to numeric ID for colors
  const getStatusId = (statusName: string | number | undefined): number | null => {
    if (!statusName) return null;

    // Si ya es un número, usarlo directamente
    if (typeof statusName === 'number') {
      return statusName;
    }

    // Mapear nombres comunes de estado a IDs
    // Normalizar: quitar acentos y convertir a minúsculas
    const normalized = statusName
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, ''); // Quitar acentos

    const originalLower = statusName.toLowerCase().trim();

    const statusNameMap: Record<string, number> = {
      'por confirmar': 1,
      pendiente: 2,
      'por preparar': 3,
      'en preparacion': 4,
      'en preparación': 4,
      picking: 4, // Estado 4: En preparación (picking)
      packing: 5, // Estado 5: En preparación (packing)
      preparado: 6,
      enviado: 7,
      devuelto: 8,
      devuélto: 8, // Variante con acento
      returned: 8, // Variante en inglés
      cancelado: 9,
      canceled: 9, // Variante en inglés
    };

    // Intentar primero con el texto normalizado (sin acentos), luego con el original
    return statusNameMap[normalized] || statusNameMap[originalLower] || null;
  };

  const getOrderStatusDotClass = (statusName: string | number | undefined): string => {
    const statusId = getStatusId(statusName);
    if (!statusId || !statusColorMap[statusId]) {
      return defaultOrderStatusDot.dot;
    }
    return statusColorMap[statusId].dot;
  };

  // Manejar click en resultado - Handle result click
  const handleResultClick = (result: GlobalSearchResult) => {
    const route = getGlobalSearchRoute(result.type, result.id, result.statusName, result.code);
    // 🎯 Usar replace: false para forzar actualización incluso si la ruta base es la misma
    navigate(route, { replace: false });
    onSearchQueryChange(''); // 🎯 Limpiar el input de búsqueda
    onOpenChange(false);
  };

  // Manejar cierre del modal - Handle modal close
  const handleClose = () => {
    onOpenChange(false);
  };

  const hasSearchQuery = searchQuery.trim().length > 0;

  return (
    <ModalCenter
      ref={modalRef}
      type="default"
      width="xl"
      onCloseModal={handleClose}
      className={`max-w-2xl [&_*]:focus:outline-none [&_*]:focus-visible:outline-none [&_button[data-testid='dialog-close']]:!hidden [&_[data-testid*='header']]:!hidden ${
        !hasSearchQuery ? '!border-0 !gap-0' : ''
      }`}
      classNameBody={`${hasSearchQuery ? 'p-4' : '!p-0'} [&_*]:focus:outline-none [&_*]:focus-visible:outline-none`}
      data-testid="global-search-modal"
    >
      {/* Input de búsqueda - Search input */}
      <div>
        <div className="relative">
          {/* Input de búsqueda - Search input */}
          <Input
            ref={searchInputRef}
            type="text"
            placeholder={t('global_search.modal_placeholder')}
            value={searchQuery}
            onChange={handleSearchInputChange}
            className={`w-full !border-0 bg-card-bg hover:bg-hover-press focus-visible:ring-0 focus-visible:outline-none h-auto !text-lg font-medium text-title px-5 py-3 !rounded-xl transition-colors ${
              searchQuery && !isLoading && totalResults > 0 ? 'pr-32' : searchQuery ? 'pr-10' : 'pr-5'
            }`}
            data-testid="global-search-modal-input"
          />

          {/* Total de resultados y botón para resetear búsqueda - Total results and reset search button (flotando a la derecha) */}
          {searchQuery && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              {/* Total de resultados - Total results */}
              {!isLoading && totalResults > 0 && (
                <span className="text-sm text-complementary-text light:text-title whitespace-nowrap">
                  {totalResults} {totalResults === 1 ? t('global_search.result') : t('global_search.results')}
                </span>
              )}
              {/* Botón para resetear búsqueda - Reset search button */}
              <button
                onClick={handleResetSearch}
                className="p-1 hover:bg-hover-press rounded transition-colors flex-shrink-0"
                data-testid="global-search-reset"
              >
                <FontAwesomeIcon icon={faXmark} className="w-4 h-4 text-icons" />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Results List y Footer solo se muestran cuando hay búsqueda - Results List and Footer only shown when there's a search */}
      {searchQuery.trim().length > 0 && (
        <>
          {/* Results List - Lista de resultados */}
          <div
            ref={resultsListRef}
            className="max-h-[400px] overflow-y-auto -mx-4 px-4"
            data-testid="global-search-results"
          >
            {isLoading ? (
              <div className="px-4 py-8 flex flex-col items-center justify-center">
                <Spinner size="md" />
              </div>
            ) : allResults.length === 0 ? (
              <div className="px-4 py-6 flex flex-col items-center justify-center">
                {/* Icono de lupa grande - Large magnifying glass icon */}
                <div className="mb-6 text-card-border">
                  <FontAwesomeIcon icon={faMagnifyingGlass} className="w-20 h-20" />
                </div>
                {/* Título principal - Main title */}
                <h3 className="text-lg font-bold text-title mb-2">{t('global_search.no_results_title')}</h3>
                {/* Descripción - Description */}
                <p className="text-sm text-complementary-text text-center max-w-sm">
                  {t('global_search.no_results_description')}
                </p>
              </div>
            ) : (
              <div className="py-2 flex flex-col gap-1">
                {/* Lista única de resultados - Single results list */}
                {allResults.map((result, index) => {
                  return (
                    <div
                      key={`${result.type}-${result.id}`}
                      data-result-index={index}
                      onClick={() => handleResultClick(result)}
                      className={`px-4 py-2 flex items-start gap-6 cursor-pointer transition-all rounded-md relative group ${
                        selectedIndex === index
                          ? 'border border-card-border'
                          : 'border border-transparent hover:border-card-border'
                      }`}
                      data-testid={`global-search-result-${index}`}
                    >
                      {/* Icono y categoría - Icon and category */}
                      {/* Columna izquierda: icono + categoría */}
                      <div className="flex items-start gap-2 flex-shrink-0 pt-0.5 w-24">
                        {getIconNode(result.type)}
                        <span className="text-xs text-icons light:text-title">{getCategoryLabel(result.type)}</span>
                      </div>

                      {/* Columna derecha: contenido */}
                      <div className="flex flex-col gap-1 flex-1 min-w-0">
                        {/* Título - Title (oculto para carritos, se muestra en badge) */}
                        {result.type !== 'cart' && (
                          <div className="text-sm font-medium text-title truncate">{result.title}</div>
                        )}

                        {/* Línea secundaria con imagen y badge - Secondary line with image and badge */}
                        {(result.type === 'location' ||
                          result.subtitle ||
                          result.code ||
                          result.imageUrl ||
                          result.metadata ||
                          result.productName ||
                          typeof result.quantity === 'number') && (
                          <div className="flex items-center gap-3">
                            {result.type === 'product' && result.imageUrl && (
                              <img
                                src={result.imageUrl}
                                alt={result.title}
                                className="w-6 h-6 rounded-full object-cover bg-card-bg flex-shrink-0"
                              />
                            )}

                            {result.type === 'order' ? (
                              <div className="flex items-center gap-2 flex-wrap">
                                {result.storeName && (
                                  <Badge className="inline-flex items-center gap-1 bg-hover-press text-title text-xs px-2 py-0.5 rounded-full border-0">
                                    <FontAwesomeIcon icon={faStore} className="w-2.5 h-2.5 text-icons" />
                                    {result.storeName}
                                  </Badge>
                                )}
                                {result.statusName && (
                                  <StatusBadgeWithDot
                                    className="text-xs rounded-full"
                                    dotClassName={getOrderStatusDotClass(result.statusName)}
                                    label={result.statusName}
                                  />
                                )}
                              </div>
                            ) : (
                              <div className="flex-1 min-w-0 flex items-center gap-2">
                                {result.type === 'location' ? (
                                  <>
                                    <div className="text-xs text-complementary-text truncate">
                                      {result.productName || t('global_search.no_product')}
                                    </div>
                                    <Badge
                                      className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border-0 ${
                                        (typeof result.quantity === 'number' ? result.quantity : 0) > 0
                                          ? 'bg-card-bg text-success'
                                          : 'bg-hover-press text-complementary-text'
                                      }`}
                                    >
                                      <FontAwesomeIcon
                                        icon={faBoxesStacked}
                                        className={`w-3 h-3 ${
                                          (typeof result.quantity === 'number' ? result.quantity : 0) > 0
                                            ? 'text-success'
                                            : 'text-complementary-text'
                                        }`}
                                      />
                                      {typeof result.quantity === 'number' ? result.quantity : 0}
                                    </Badge>
                                  </>
                                ) : (
                                  <>
                                    {result.type !== 'product' &&
                                      result.type !== 'cart' &&
                                      result.type !== 'preparation' &&
                                      result.type !== 'returned_reception' &&
                                      result.subtitle && (
                                        <div className="text-xs text-complementary-text truncate">
                                          {result.subtitle}
                                        </div>
                                      )}
                                    {result.metadata && (
                                      <div className="text-xs text-complementary-text truncate">{result.metadata}</div>
                                    )}
                                    {result.type === 'product' && result.code && (
                                      <Badge className="text-xs px-2 py-0.5 rounded-full bg-bg-info text-info flex-shrink-0 uppercase -ml-1 border-0">
                                        {result.code}
                                      </Badge>
                                    )}
                                    {result.type === 'cart' && result.code && (
                                      <Badge className="text-xs px-2 py-0.5 rounded-full bg-bg-success text-success flex-shrink-0 border-0">
                                        {result.code}
                                      </Badge>
                                    )}
                                    {result.type === 'merchandise' && typeof result.statusName === 'number' && (
                                      <MerchandiseStatusBadge
                                        status={result.statusName}
                                        size="sm"
                                        className="!border-0"
                                      />
                                    )}
                                    {result.type === 'preparation' && typeof result.statusName === 'number' && (
                                      <PreparationStatusBadge status={result.statusName} />
                                    )}
                                    {result.type === 'returned_reception' && (
                                      <Badge
                                        className={`text-xs px-2 py-0.5 rounded-full border-0 ${typeof result.statusName === 'number' ? getReturnedStatusBadgeClass(result.statusName) : 'bg-hover-press text-complementary-text'}`}
                                      >
                                        {typeof result.statusName === 'number'
                                          ? getReturnedStatusName(result.statusName, t)
                                          : t('global_search.no_status')}
                                      </Badge>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Botón "Ir" en la esquina inferior derecha - "Go" button in bottom right corner (solo visible en hover/focus) */}
                      <div
                        className={`absolute bottom-2 right-2 flex items-center gap-1 text-xs text-complementary-text light:text-title transition-opacity ${
                          selectedIndex === index ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                        }`}
                      >
                        <span>{t('global_search.go')}</span>
                        <EnterIcon className="w-3 h-3" />
                      </div>
                    </div>
                  );
                })}

                {/* Elemento de referencia para infinite scroll - Reference element for infinite scroll */}
                {hasNextPage && (
                  <div ref={loadMoreRef} className="py-4 flex justify-center">
                    {isFetchingNextPage && <Spinner size="sm" />}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer - Pie de página */}
          <div className="flex items-center px-8 py-3 -mx-4 -mb-4 mt-4 gap-6">
            <div className="flex items-center gap-2 text-xs text-icons">
              <span>{t('global_search.navigate')}</span>
              <div className="flex items-center gap-1 rounded border border-card-border bg-card-bg text-xs text-title h-5 w-5 justify-center">
                <FontAwesomeIcon icon={faArrowUp} className="w-2 h-2" />
              </div>
              <div className="flex items-center gap-1 rounded border border-card-border bg-card-bg text-xs text-title h-5 w-5 justify-center">
                <FontAwesomeIcon icon={faArrowDown} className="w-2 h-2" />
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-icons">
              <span>{t('global_search.close')}</span>
              <div className="flex items-center gap-1 rounded border border-card-border bg-card-bg text-xs text-title h-5 px-1.5 justify-center">
                ESC
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-icons">
              <span>{t('global_search.open')}</span>
              <div className="flex items-center gap-1 rounded border border-card-border bg-card-bg text-xs text-title h-5 px-1.5 justify-center">
                ENTER
              </div>
            </div>
          </div>
        </>
      )}
    </ModalCenter>
  );
};
